import React, { useCallback, useEffect, useState, useRef, Children, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { CaseStudyPage as CaseStudyImage } from '../components/CaseStudyImage';
interface AccountCaseStudyPageProps {
  onBack: () => void;
  onNext?: () => void;
  nextLabel?: string;
}
const sections = [{
  id: 'problem',
  label: 'The Problem'
}, {
  id: 'business-case',
  label: 'So What?'
}, {
  id: 'precedents',
  label: 'Competitive Analysis'
}, {
  id: 'constraints',
  label: 'Constraints'
}, {
  id: 'attribute-overlap',
  label: 'Attribute Overlap'
}, {
  id: 'design-principles',
  label: 'Design Principles'
}, {
  id: 'handoff',
  label: 'Hand-off'
}, {
  id: 'prototype',
  label: 'Prototype'
}, {
  id: 'key-lessons',
  label: 'Key Lessons'
}];
export function AccountCaseStudyPage({
  onBack,
  onNext,
  nextLabel = 'Next Project'
}: AccountCaseStudyPageProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const isScrollingRef = useRef(false);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? Math.round(scrollTop / docHeight * 100) : 0;
      setScrollProgress(Math.min(100, Math.max(0, progress)));
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      if (isScrollingRef.current) return;
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const idx = sections.findIndex(s => s.id === entry.target.id);
          if (idx !== -1) setActiveIndex(idx);
        }
      });
    }, {
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0
    });
    sections.forEach(s => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);
  const scrollTo = useCallback((index: number) => {
    const clamped = Math.max(0, Math.min(index, sections.length - 1));
    const el = document.getElementById(sections[clamped].id);
    if (el) {
      isScrollingRef.current = true;
      setActiveIndex(clamped);
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
      setTimeout(() => {
        isScrollingRef.current = false;
      }, 800);
    }
  }, []);
  const fadeInUp = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  };
  const staggerContainer = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  return <div className="min-h-screen bg-[#F8F6F3] text-[#6B6560] selection:bg-[#1A1A1A]/10 selection:text-[#1A1A1A]" data-id="element-189">
      <div className="max-w-5xl mx-auto px-8 md:px-16 py-12 md:py-20 pb-32" data-id="element-190">
        {/* Back Navigation */}
        <motion.button onClick={() => {
        onBack();
        setTimeout(() => {
          document.getElementById('work')?.scrollIntoView({
            behavior: 'smooth'
          });
        }, 100);
      }} initial={{
        opacity: 0,
        x: -10
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.2
      }} className="group flex items-center gap-2 text-sm font-medium text-[#6B6560] hover:text-[#1A1A1A] mb-12 transition-colors" data-id="element-191">
          <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" data-id="element-192" />
          Back to Work
        </motion.button>

        {/* Hero Section */}
        <motion.section initial="hidden" animate="visible" variants={staggerContainer} className="mb-24 md:mb-32" data-id="element-193">
          <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1A1A1A] mb-6 leading-tight" data-id="element-194">
            One Account
          </motion.h1>
          <motion.p variants={fadeInUp} className="text-xl md:text-2xl text-[#6B6560] max-w-3xl leading-relaxed mb-12" data-id="element-195">
            Centralizing account management across for grocery, apparel, health
            and beauty brands. Reducing overhead, enabling cross-brand features,
            and simplifying onboarding for millions of Canadians.
          </motion.p>

          <motion.div variants={fadeInUp} className="grid grid-cols-2 md:grid-cols-4 gap-8 border-t border-[#E8E4DF] pt-8 mb-16" data-id="element-196">
            <div data-id="element-197">
              <h3 className="text-xs font-bold uppercase tracking-widest text-[#A09A94] mb-2" data-id="element-198">
                Timeframe
              </h3>
              <p className="text-[#1A1A1A]" data-id="element-199">2023-2024</p>
            </div>
            <div data-id="element-200">
              <h3 className="text-xs font-bold uppercase tracking-widest text-[#A09A94] mb-2" data-id="element-201">
                Roles
              </h3>
              <div className="space-y-1" data-id="element-202">
                <p className="text-[#1A1A1A]" data-id="element-203">Lead Product Designer</p>
                <p className="text-[#1A1A1A]" data-id="element-204">Privacy Design Lead</p>
                <p className="text-[#1A1A1A]" data-id="element-205">Stakeholder Liaison</p>
              </div>
            </div>
            <div data-id="element-206">
              <h3 className="text-xs font-bold uppercase tracking-widest text-[#A09A94] mb-2" data-id="element-207">
                Tools
              </h3>
              <div className="space-y-1" data-id="element-208">
                <p className="text-[#1A1A1A]" data-id="element-209">Figma</p>
                <p className="text-[#1A1A1A]" data-id="element-210">Miro</p>
                <p className="text-[#1A1A1A]" data-id="element-211">Coda</p>
                <p className="text-[#1A1A1A]" data-id="element-212">Token Studio</p>
              </div>
            </div>
            <div data-id="element-213">
              <h3 className="text-xs font-bold uppercase tracking-widest text-[#A09A94] mb-2" data-id="element-214">
                Design Team Size
              </h3>
              <p className="text-[#1A1A1A]" data-id="element-215">2</p>
            </div>
          </motion.div>

          <motion.div variants={fadeInUp} className="w-full rounded-2xl overflow-hidden mb-6" data-id="element-216">
            <video src="https://cdn.magicpatterns.com/uploads/3FCj61wQk54xiUsjKVuVLa/Account-Header.mp4" autoPlay loop muted playsInline className="w-full h-auto block" data-id="element-217" />
          </motion.div>

          {/* Second hero image */}
          <motion.div variants={fadeInUp} className="w-full rounded-2xl overflow-hidden mt-6" data-id="element-218"></motion.div>

          <motion.div variants={fadeInUp} className="w-full rounded-2xl overflow-hidden mt-6" data-id="element-219"></motion.div>
        </motion.section>

        {/* The Problem */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24 md:mb-32 scroll-mt-8" data-id="element-220">
          <motion.h2 id="problem" variants={fadeInUp} className="text-3xl font-bold text-[#1A1A1A] mb-6 scroll-mt-8" data-id="element-221">
            The Problem
          </motion.h2>
          <motion.div variants={fadeInUp} className="space-y-6 text-lg leading-relaxed text-[#6B6560]" data-id="element-222">
            <p data-id="element-223">
              Account management was done differently across our businesses, and
              onboarding was often redundant. For example, a customer who used
              PC Express, Shoppers Drug Mart, and Joe Fresh had one PCid account
              but could access it in 3 different ways, using a combination of
              different websites.
            </p>
          </motion.div>

          <motion.blockquote variants={fadeInUp} className="my-16 pl-8 border-l-2 border-[#E8E4DF] text-2xl md:text-3xl italic text-[#1A1A1A] leading-normal max-w-4xl" data-id="element-224">
            "How do you unify account systems when each brand has their own
            approach?"
          </motion.blockquote>

          <motion.div variants={fadeInUp} className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12" data-id="element-225">
            <div className="bg-white p-8 rounded-2xl border border-[#E8E4DF]" data-id="element-226">
              <h3 className="text-xs font-bold uppercase tracking-widest text-[#A09A94] mb-4" data-id="element-227">
                Before
              </h3>
              <p className="text-sm text-[#6B6560] mb-4" data-id="element-228">
                Password, Email, and MFA are all within{' '}
                <span className="font-medium text-[#1A1A1A]" data-id="element-229">
                  myaccount.pcid.ca
                </span>
              </p>
            </div>
            <div className="bg-white p-8 rounded-2xl border border-[#E8E4DF]" data-id="element-230">
              <h3 className="text-xs font-bold uppercase tracking-widest text-[#A09A94] mb-4" data-id="element-231">
                After
              </h3>
              <p className="text-sm text-[#6B6560]" data-id="element-232">
                Password, email, MFA, phone number, shipping address, payment
                methods, and marketing communications will all live within{' '}
                <span className="font-bold text-[#1A1A1A]" data-id="element-233">One Account</span>
              </p>
            </div>
          </motion.div>
        </motion.section>

        {/* So What? */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24 md:mb-32" data-id="element-234">
          <motion.h2 id="business-case" variants={fadeInUp} className="text-3xl font-bold text-[#1A1A1A] mb-6 scroll-mt-8" data-id="element-235">
            So What?
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-236">
            {[{
            title: 'Maintaining 5 things = $$$',
            desc: 'By amalgamating to one central account, we reduce our overhead and maintenance costs significantly.'
          }, {
            title: 'Limits our ability to launch features',
            desc: 'If one LOB wants an account-based feature, we can make it available for all LOBs instantly with a centralized system.'
          }, {
            title: 'Convoluted onboarding',
            desc: 'Our onboarding for businesses like PCX and PCO includes form fields we can already fill if the user has a PCid account.'
          }, {
            title: 'Better brand comprehension',
            desc: 'Housing everything under the One Account umbrella will help our customers better understand the Loblaw ecosystem.'
          }].map((card, i) => <motion.div key={i} variants={fadeInUp} className="bg-white p-8 rounded-2xl border border-[#E8E4DF]" data-id="element-237">
                <h3 className="text-xl font-bold text-[#1A1A1A] mb-3" data-id="element-238">
                  {card.title}
                </h3>
                <p className="leading-relaxed text-[#6B6560] text-sm" data-id="element-239">
                  {card.desc}
                </p>
              </motion.div>)}
          </div>
        </motion.section>

        {/* Competitive Analysis */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24 md:mb-32" data-id="element-240">
          <motion.h2 id="precedents" variants={fadeInUp} className="text-3xl font-bold text-[#1A1A1A] mb-6 scroll-mt-8" data-id="element-241">
            Competitive analysis
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg leading-relaxed mb-12 max-w-3xl text-[#6B6560]" data-id="element-242">
            Major companies with multi-brand ecosystems had already centralized
            their account management. We studied their approaches to inform our
            own.
          </motion.p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-243">
            {[{
            title: 'Apple',
            desc: 'Apple Account Centre (RIP "Apple ID") is where all account information is managed across every Apple service.',
            image: 'https://cdn.magicpatterns.com/uploads/3eM7QucA4VXvQeundsSAR6/Apple.png'
          }, {
            title: 'Meta',
            desc: "Whether it's Instagram, WhatsApp, or Facebook — your account information is handled in one place.",
            image: 'https://cdn.magicpatterns.com/uploads/k1NzxgyUKtjqQ1wVQ8C2Zi/Meta.png'
          }, {
            title: 'GAP',
            desc: 'Multiple brands with distinct look and feels all managed under one unified account.',
            image: 'https://cdn.magicpatterns.com/uploads/8C3oMSqotVHmBFngkcdW5t/GAP.png'
          }].map((card, i) => <motion.div key={i} variants={fadeInUp} className="bg-white rounded-2xl border border-[#E8E4DF] overflow-hidden" data-id="element-244">
                <div className="w-full aspect-[4/3] bg-[#F0EDEA] flex items-center justify-center p-4" data-id="element-245">
                  <img src={card.image} alt={`${card.title} account management`} className="max-w-full max-h-full object-contain rounded-lg" data-id="element-246" />
                </div>
                <div className="p-8" data-id="element-247">
                  <h3 className="text-xl font-bold text-[#1A1A1A] mb-3" data-id="element-248">
                    {card.title}
                  </h3>
                  <p className="leading-relaxed text-[#6B6560] text-sm" data-id="element-249">
                    {card.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </motion.section>

        {/* Our Key Constraints */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24 md:mb-32" data-id="element-250">
          <motion.h2 id="constraints" variants={fadeInUp} className="text-3xl font-bold text-[#1A1A1A] mb-6 scroll-mt-8" data-id="element-251">
            Our Key Constraints
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg leading-relaxed mb-12 max-w-3xl text-[#6B6560]" data-id="element-252">
            We used Miro workshops with our stakeholders to gain alignment on
            our current experiences and their divergences. Through this process,
            three key constraints emerged.
          </motion.p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16" data-id="element-253">
            {[{
            title: 'Resourcing',
            desc: 'Balancing the "blue sky" vision for an account experience vs what\'s feasible within our time and budgetary constraints.'
          }, {
            title: 'Privacy / Legal',
            desc: 'Handling personal information comes with an added regulatory dimension to every design we make.'
          }, {
            title: 'Dev Onboarding',
            desc: 'This project required multiple different development teams with different contexts to collaborate.'
          }].map((card, i) => <motion.div key={i} variants={fadeInUp} className="bg-white p-8 rounded-2xl border border-[#E8E4DF]" data-id="element-254">
                <h3 className="text-xl font-bold text-[#1A1A1A] mb-3" data-id="element-255">
                  {card.title}
                </h3>
                <p className="leading-relaxed text-[#6B6560] text-sm" data-id="element-256">
                  {card.desc}
                </p>
              </motion.div>)}
          </div>
        </motion.section>

        {/* Attribute Overlap */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24 md:mb-32" data-id="element-257">
          <motion.h2 id="attribute-overlap" variants={fadeInUp} className="text-3xl font-bold text-[#1A1A1A] mb-6 scroll-mt-8" data-id="element-258">
            Attribute Overlap
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg leading-relaxed mb-12 max-w-3xl text-[#6B6560]" data-id="element-259">
            After many workshops, we agreed upon which attributes should move to
            One Account and which should stay on the LOB. We then validated this
            with user tests, which validated our decision.
          </motion.p>

          <motion.div variants={fadeInUp} className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-260">
            <div className="flex flex-col gap-8" data-id="element-261">
              <div className="bg-white p-8 rounded-2xl border border-[#E8E4DF]" data-id="element-262">
                <h3 className="text-xl font-bold text-[#1A1A1A] mb-6" data-id="element-263">
                  One Account
                </h3>
                <ul className="space-y-3 text-[#6B6560]" data-id="element-264">
                  {['Password', 'Email', 'MFA', 'Phone number', 'Shipping address', 'Payment methods', 'Marketing communications'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-265">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" data-id="element-266" />
                      <span data-id="element-267">{item}</span>
                    </li>)}
                </ul>
              </div>
              <div className="bg-white p-8 rounded-2xl border border-[#E8E4DF]" data-id="element-268">
                <h3 className="text-xl font-bold text-[#1A1A1A] mb-6" data-id="element-269">
                  Unique to LOB
                </h3>
                <ul className="space-y-3 text-[#6B6560]" data-id="element-270">
                  {['Order history', 'LOB-specific features', 'PC Optimum', 'Favourites (SDM)'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-271">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#A09A94] shrink-0" data-id="element-272" />
                      <span data-id="element-273">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>

            <div className="bg-[#E8E4DF] rounded-2xl overflow-hidden flex items-center justify-center min-h-[400px] p-6" data-id="element-274">
              <img src="https://cdn.magicpatterns.com/uploads/7VPt8CXVChQQ7b2zDyv8ub/Phone_frame.png" alt="Account Centre designs — Profile, Email & Password, Security, Shipping, Payment, Preferences" className="max-w-full max-h-[500px] object-contain" data-id="element-275" />
            </div>
          </motion.div>
        </motion.section>

        {/* Design Principles */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24 md:mb-32" data-id="element-276">
          <motion.h2 id="design-principles" variants={fadeInUp} className="text-3xl font-bold text-[#1A1A1A] mb-6 scroll-mt-8" data-id="element-277">
            Design Principles
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg leading-relaxed mb-12 max-w-3xl text-[#6B6560]" data-id="element-278">
            Living with constraints and timelines, we leaned on Helios
            components and used theming to our advantage — re-using existing
            patterns while adapting the experience per brand.
          </motion.p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-279">
            <motion.div variants={fadeInUp} className="bg-white p-8 rounded-2xl border border-[#E8E4DF]" data-id="element-280">
              <h3 className="text-xl font-bold text-[#1A1A1A] mb-3" data-id="element-281">
                Flexibility
              </h3>
              <p className="leading-relaxed text-[#6B6560] text-sm" data-id="element-282">
                We want to give users choice — what information would they like
                to provide us? The system adapts to their preferences rather
                than demanding everything upfront.
              </p>
            </motion.div>
            <motion.div variants={fadeInUp} className="bg-white p-8 rounded-2xl border border-[#E8E4DF]" data-id="element-283">
              <h3 className="text-xl font-bold text-[#1A1A1A] mb-3" data-id="element-284">
                Transparency
              </h3>
              <p className="leading-relaxed text-[#6B6560] text-sm" data-id="element-285">
                We want users to understand how we are using their personal
                information and consent. Every data access is visible and
                controllable.
              </p>
            </motion.div>
          </div>

          <motion.div variants={fadeInUp} className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-286">
            <div className="bg-white p-8 rounded-2xl border border-[#E8E4DF]" data-id="element-287">
              <h3 className="text-xl font-bold text-[#1A1A1A] mb-3" data-id="element-288">
                Helios Components
              </h3>
              <p className="leading-relaxed text-[#6B6560] text-sm" data-id="element-289">
                Re-using existing design system components to maintain
                consistency and reduce development time. PC Pay iframes were
                integrated where needed.
              </p>
            </div>
            <div className="bg-white p-8 rounded-2xl border border-[#E8E4DF]" data-id="element-290">
              <h3 className="text-xl font-bold text-[#1A1A1A] mb-3" data-id="element-291">
                Using Theming to Our Advantage
              </h3>
              <p className="leading-relaxed text-[#6B6560] text-sm" data-id="element-292">
                Each brand gets its own visual identity within the same
                underlying account structure — one codebase, many faces.
              </p>
            </div>
          </motion.div>
        </motion.section>

        {/* Hand-off Files */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24 md:mb-32" data-id="element-293">
          <motion.h2 id="handoff" variants={fadeInUp} className="text-3xl font-bold text-[#1A1A1A] mb-6 scroll-mt-8" data-id="element-294">
            Hand-off Files
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg leading-relaxed mb-12 max-w-3xl text-[#6B6560]" data-id="element-295">
            Our FE team was working with Helios components for the first time.
            We adapted our hand-off process to be more detailed, more
            async-friendly, and continuously improving.
          </motion.p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16" data-id="element-296">
            {[{
            title: 'Component Annotations',
            desc: 'Our FE team was new to Helios — HMW be empathetic to their needs? Be a bit more detailed in Figma.'
          }, {
            title: 'Async-First Handoff',
            desc: "Our tech partners didn't respond well to synchronous meetings (different time zones!) — HMW adapt to more async ways of handoff?"
          }, {
            title: 'Still Iterating Our Process',
            desc: 'Development recently began. Observe what kinds of questions come up frequently and HMW improve hand-off files for future features?'
          }].map((card, i) => <motion.div key={i} variants={fadeInUp} className="bg-white p-8 rounded-2xl border border-[#E8E4DF]" data-id="element-297">
                <h3 className="text-lg font-bold text-[#1A1A1A] mb-3" data-id="element-298">
                  {card.title}
                </h3>
                <p className="text-sm leading-relaxed text-[#6B6560]" data-id="element-299">
                  {card.desc}
                </p>
              </motion.div>)}
          </div>

          <motion.h3 variants={fadeInUp} className="text-xl font-bold text-[#1A1A1A] mb-8" data-id="element-300">
            Hand-off File Sections
          </motion.h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-301">
            {[{
            title: '"Main" Flow',
            desc: 'Primary screens by LOB and breakpoint. How might the user control their Preference settings across different brands? Screens for PC Optimum, Grocery across Desktop, Tablet, and Mobile.'
          }, {
            title: 'Interactions',
            desc: 'Edge cases, alerts, tab behaviour, error states. Show confirmation if save successful. Autoscroll to centre the active tab. Show error if save unsuccessful, return to previous state.'
          }, {
            title: 'Component ID + Spacing',
            desc: 'What web components are we using? What variants? What spacing between components? Examples: Header Group 01, Header Group 03, Checkbox Item, Standard Button, Rich Text.'
          }, {
            title: 'Accessibility',
            desc: 'Recommended labels for control groups, correct heading tags. Annotations include page headings, chip carousel a11y docs, section headings, and input types.'
          }].map((card, i) => <motion.div key={i} variants={fadeInUp} className="bg-white p-8 rounded-2xl border border-[#E8E4DF]" data-id="element-302">
                <h3 className="text-lg font-bold text-[#1A1A1A] mb-3" data-id="element-303">
                  {card.title}
                </h3>
                <p className="text-sm leading-relaxed text-[#6B6560]" data-id="element-304">
                  {card.desc}
                </p>
              </motion.div>)}
          </div>
        </motion.section>

        {/* Responsive Prototype */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24" data-id="element-305">
          <motion.h2 id="prototype" variants={fadeInUp} className="text-3xl font-bold text-[#1A1A1A] mb-6 scroll-mt-8" data-id="element-306">
            Responsive Prototype
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg leading-relaxed mb-12 max-w-3xl text-[#6B6560]" data-id="element-307">
            We used the Responsively plugin in Figma to create responsive
            prototypes . This let us accompany our static responsive
            documentation with a living, interactive prototype.
          </motion.p>

          <motion.div variants={fadeInUp} className="w-full rounded-2xl overflow-hidden mb-8 shadow-sm border border-[#E8E4DF]" data-id="element-308">
            <video src="https://cdn.magicpatterns.com/uploads/3Scb66PNxhBLVcWFhcHYxM/Screen Recording 2026-04-27 at 11.48.51 AM.mov" autoPlay loop muted playsInline className="w-full h-full object-cover block" data-id="element-309" />
          </motion.div>
        </motion.section>

        {/* Key Lessons */}
        <motion.section initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={staggerContainer} className="mb-24" data-id="element-310">
          <motion.h2 id="key-lessons" variants={fadeInUp} className="text-3xl font-bold text-[#1A1A1A] mb-9 scroll-mt-8" data-id="element-311">
            Key Lessons
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-312">
            {[{
            title: 'Compromise is Key',
            desc: "Taking the time to understand each stakeholder's preferences and discipline was pivotal to suiting their needs. Working with teams ranging from legal to development helped us fine tune the final product."
          }, {
            title: 'Learn How Things Are Built',
            desc: "When we first set off to build the One Account experience, we didn't know how it was gonna come together. Taking the time to understand the nuances between an iframe vs natively building something helped us improve the way we worked with product and tech teams."
          }].map((lesson, i) => <motion.div key={i} variants={fadeInUp} className="bg-white p-8 rounded-2xl border border-[#E8E4DF]" data-id="element-313">
                <h3 className="text-lg font-bold text-[#1A1A1A] mb-3" data-id="element-314">
                  {lesson.title}
                </h3>
                <p className="text-sm leading-relaxed text-[#6B6560]" data-id="element-315">
                  {lesson.desc}
                </p>
              </motion.div>)}
          </div>
        </motion.section>

        {/* Bottom Project Navigation */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="border-t border-[#E8E4DF] pt-12 flex justify-between items-center" data-id="element-316">
          <button onClick={() => {
          onBack();
          setTimeout(() => {
            document.getElementById('work')?.scrollIntoView({
              behavior: 'smooth'
            });
          }, 100);
        }} className="text-[#1A1A1A] hover:text-[#6B6560] transition-colors text-sm font-medium" data-id="element-317">
            ← Back to all work
          </button>
          <button onClick={onNext} className="group text-sm font-medium text-[#1A1A1A] hover:text-[#6B6560] transition-all duration-200 hover:translate-x-1" data-id="element-318">
            Next Project: {nextLabel} →
          </button>
        </motion.div>
      </div>

      {/* Fixed Section Navigator */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.6
    }} className="fixed bottom-20 md:bottom-8 left-0 right-0 md:left-16 lg:left-56 flex items-center justify-center gap-2 z-40 px-4" data-id="element-319">
        <div className="flex items-center gap-3 bg-white/90 backdrop-blur-md border border-[#E8E4DF] rounded-full px-2 py-2 shadow-lg shadow-black/5" data-id="element-320">
          <button onClick={() => scrollTo(activeIndex - 1)} disabled={activeIndex === 0} className="w-10 h-10 rounded-full border border-[#E8E4DF] flex items-center justify-center text-[#1A1A1A] hover:bg-[#F0EDEA] disabled:opacity-30 disabled:hover:bg-transparent transition-colors shrink-0" aria-label="Previous section" data-id="element-321">
            <ChevronLeft size={18} data-id="element-322" />
          </button>

          <AnimatePresence mode="wait" data-id="element-323">
            <motion.span key={activeIndex} initial={{
            opacity: 0,
            y: 4
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -4
          }} transition={{
            duration: 0.15
          }} className="text-sm font-medium text-[#1A1A1A] w-[160px] text-center px-2 select-none" data-id="element-324">
              {sections[activeIndex].label}
            </motion.span>
          </AnimatePresence>

          <button onClick={() => scrollTo(activeIndex + 1)} disabled={activeIndex === sections.length - 1} className="w-10 h-10 rounded-full border border-[#E8E4DF] flex items-center justify-center text-[#1A1A1A] hover:bg-[#F0EDEA] disabled:opacity-30 disabled:hover:bg-transparent transition-colors shrink-0" aria-label="Next section" data-id="element-325">
            <ChevronRight size={18} data-id="element-326" />
          </button>
        </div>

        <motion.span key={scrollProgress} initial={{
        opacity: 0.8
      }} animate={{
        opacity: 1
      }} className="bg-[#1A1A1A] text-white text-xs font-semibold tabular-nums rounded-full px-3 select-none shrink-0 w-[46px] text-center shadow-lg shadow-black/10 h-12 flex items-center justify-center" data-id="element-327">
          {scrollProgress}%
        </motion.span>
      </motion.div>
    </div>;
}